import callGraphQLApi from '@helpers/callGraphQLApi';
import {
  FETCH_SPEAKER_SEPARATION_RESULTS,
  FETCH_SPEAKER_SEPARATION_RESULTS_FAILURE,
  FETCH_SPEAKER_SEPARATION_RESULTS_SUCCESS,
  FETCH_TRANSCRIPTION_RESULTS,
  FETCH_TRANSCRIPTION_RESULTS_FAILURE,
  FETCH_TRANSCRIPTION_RESULTS_SUCCESS,
} from '../actions';
import { FetchEngineResultsRequest } from '../models';
import { TranscriptionServiceResponse } from '../models/services';
import { selectCurrentTdoId } from '../selectors/tdo';
import { Thunk } from './interfaces';

type GraphQLApiCallParams<T> = Parameters<typeof callGraphQLApi<T>>[0];
type EngineResultsGraphQLActionTypes = Readonly<
  GraphQLApiCallParams<TranscriptionServiceResponse>['actionTypes']
>;

const ENGINE_RESULTS_BY_CATEGORY_QUERY = `
    query($tdoId: ID!, $categoryId: ID!, $startOffset: Int, $stopOffset: Int) {
      engineResults(tdoId: $tdoId, engineCategoryIds: [$categoryId], startOffsetMs: $startOffset, stopOffsetMs: $stopOffset) {
        records {
          tdoId
          engineId
          assetId
          startOffsetMs
          stopOffsetMs
          jsondata
        }
      }
    }`;

const ENGINE_RESULTS_BY_ENGINE_IDS_QUERY = `
      query($tdoId: ID!, $engines: [ID!], $startOffset: Int, $stopOffset: Int) {
        engineResults(tdoId: $tdoId, engineIds: $engines, startOffsetMs: $startOffset, stopOffsetMs: $stopOffset) {
          records {
            tdoId
            engineId
            assetId
            startOffsetMs
            stopOffsetMs
            jsondata
          }
        }
      }
    `;

/**
 * Drop any record that does not belong to the media that is open NOW.
 *
 * Two different ways a response can be wrong, and one test catches both:
 *
 * - The response is for the media we asked about, but the user has moved on
 *   since. Nothing cancels a fetch in flight, so a late reply would otherwise
 *   be written over whatever is open — rendering one recording's speaker
 *   boundaries on another's transcript, and leaving that recording's asset ids
 *   behind for Delete Speaker Separation to act on.
 * - The API returned a record for a TDO nobody asked about.
 *
 * Comparing against what is open now, rather than against what this request
 * asked for, is the whole point: a stale response contains exactly the records
 * it was asked for, so testing it against its own request always passes it.
 *
 * The reducer and the outbound merge both read these records, so filtering
 * here covers both.
 */
const forCurrentTdo = (
  response: TranscriptionServiceResponse | undefined,
  tdoId: string
): TranscriptionServiceResponse | undefined => {
  const records = response?.engineResults?.records;
  if (!records?.length) {
    return response;
  }

  const mine = records.filter(
    (record) => !record.tdoId || record.tdoId === tdoId
  );
  if (mine.length === records.length) {
    return response;
  }

  console.error(
    `[Data Validation Error] dropped ${
      records.length - mine.length
    } engineResults record(s) that do not belong to TDO ${tdoId}.`
  );

  return {
    ...response,
    engineResults: { ...response?.engineResults, records: mine },
  } as TranscriptionServiceResponse;
};

const createFetchEngineResultsThunk =
  (
    actionTypes: EngineResultsGraphQLActionTypes
  ): Thunk<FetchEngineResultsRequest, unknown> =>
  ({ tdoId, categoryId, engines, startOffset = 0, stopOffset = 0 }) =>
  async (dispatch, getState) => {
    const useEngineIds = !!engines?.length;
    const query = useEngineIds
      ? ENGINE_RESULTS_BY_ENGINE_IDS_QUERY
      : ENGINE_RESULTS_BY_CATEGORY_QUERY;

    // the tuple holds action creators here, not type strings
    const [, success] = actionTypes;
    const successType = typeof success === 'string' ? success : success?.type;

    return await callGraphQLApi<TranscriptionServiceResponse>({
      actionTypes,
      query,
      variables: {
        tdoId,
        ...(useEngineIds ? { engines } : { categoryId }),
        startOffset: Math.trunc(startOffset),
        stopOffset: Math.ceil(stopOffset),
      },
      dispatch: (action: { type: string; payload?: unknown }) => {
        dispatch(
          action.type === successType
            ? {
                ...action,
                payload: forCurrentTdo(
                  action.payload as TranscriptionServiceResponse | undefined,
                  // read at RESPONSE time, so navigating away between the
                  // request and the reply changes the answer
                  selectCurrentTdoId(getState()) ?? tdoId
                ),
              }
            : action
        );
      },
      getState,
      bailout: undefined,
    });
  };

export type FetchTranscriptionResultsResponse = TranscriptionServiceResponse;
export type FetchSpeakerSeparationResultsResponse =
  TranscriptionServiceResponse;

export const fetchTranscriptionResults = createFetchEngineResultsThunk([
  FETCH_TRANSCRIPTION_RESULTS,
  FETCH_TRANSCRIPTION_RESULTS_SUCCESS,
  FETCH_TRANSCRIPTION_RESULTS_FAILURE,
]);

export const fetchSpeakerSeparationResults = createFetchEngineResultsThunk([
  FETCH_SPEAKER_SEPARATION_RESULTS,
  FETCH_SPEAKER_SEPARATION_RESULTS_SUCCESS,
  FETCH_SPEAKER_SEPARATION_RESULTS_FAILURE,
]);
